import { Local } from '@/entities/auth/local.enum';
import { InvoiceType } from '@/entities/invoices/invoice-type.enum';
import type { ReceiverData } from '@/entities/invoices/invoice.entity';
import { COUNTRY_CODES } from '@/utils/countries';
import { normalizeVatId } from '@/utils/vat-id';
import { vatIdSchema } from './vat-id.schema';
import { TaxProcedure } from '@carvia/ros-client-types';
import { CounterpartyType } from '@/entities/ledger/ledger-entry.entity';
import { type MaybeRefOrGetter, toRef } from '@vueuse/core';
import * as yup from 'yup';

const invoiceCreateCounterpartyTypes = [
  CounterpartyType.CUSTOMER,
  CounterpartyType.AGENCY,
  CounterpartyType.OTHER,
];

export function createInvoiceCreateSchema(options?: {
  counterpartyVatId?: MaybeRefOrGetter<string | null | undefined>;
}) {
  return yup.object().shape(
    {
      title: yup.string().required(),
      counterpartyType: yup.string().oneOf(invoiceCreateCounterpartyTypes).optional(),
      positions: yup.array(
        yup.object({
          title: yup.string().required(),
          amount: yup.number().required(),
          unitPrice: yup
            .number()
            .required()
            .test('notNegative', 'Price must be negative', function (value) {
              return this.from?.[1].value.type === InvoiceType.REVERSAL
                ? yup.number().max(0).isValidSync(value)
                : true;
            }),
          tax: yup.number().required(),
          lane: yup.string().optional(),
          counterpartyType: yup.string().optional(),
        }),
      ),
      comment: yup.string().optional(),
      type: yup
        .string()
        .oneOf(
          Object.values([
            InvoiceType.DAMAGE,
            InvoiceType.CANCELLATION_FEE,
            InvoiceType.ADMINISTRATIVE_OFFENCE,
            InvoiceType.REVERSAL,
            InvoiceType.OTHER,
            InvoiceType.GENERIC,
            InvoiceType.CAR_SALE,
            InvoiceType.COUPON,
            InvoiceType.SELF_BILLING,
            InvoiceType.CONTRACT_PENALTY,
          ]),
        )
        .required(),
      originalInvoiceId: yup.string().when('type', {
        is: (type: InvoiceType) => type === InvoiceType.REVERSAL,
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.notRequired(),
      }),
      receiverData: yup
        .object({
          city: yup.string().required(),
          company: yup.string().optional(),
          country: yup
            .string()
            .trim()
            .uppercase()
            .oneOf([...COUNTRY_CODES])
            .required(),
          email: yup.string().required().email(),
          firstName: yup.string().required(),
          lastName: yup.string().required(),
          preferredLocal: yup.string().oneOf(Object.values(Local)).required(),
          street: yup.string().required(),
          vatId: vatIdSchema()
            .optional()
            .nullable()
            .test(
              'reverseChargeRequiresVatId',
              'VAT ID is required for reverse charge',
              function (value) {
                const taxProcedure = this.from?.[1]?.value?.taxProcedure;
                if (taxProcedure === TaxProcedure.REVERSE_CHARGE) {
                  return !!normalizeVatId(value);
                }
                return true;
              },
            ),
          zip: yup.string().required(),
        })
        .default(undefined)
        .when(['type', 'customerId'], {
          is: (type: InvoiceType, customerId: string | undefined) => {
            return type === InvoiceType.GENERIC && !customerId;
          },
          then: (schema) => schema.required(),
          otherwise: (schema) => schema.notRequired(),
        }),
      customerId: yup.string().when(['type', 'receiverData'], {
        is: (type: InvoiceType, receiverData: ReceiverData | undefined) =>
          type === InvoiceType.GENERIC && !receiverData,
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.notRequired(),
      }),
      bookingId: yup.string().when('type', {
        is: (type: InvoiceType) =>
          type === InvoiceType.DAMAGE ||
          type === InvoiceType.CANCELLATION_FEE ||
          type === InvoiceType.ADMINISTRATIVE_OFFENCE ||
          type === InvoiceType.CONTRACT_PENALTY ||
          type === InvoiceType.OTHER,
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.notRequired(),
      }),
      taxProcedure: yup
        .string()
        .oneOf(Object.values(TaxProcedure))
        .optional()
        .default(TaxProcedure.REGULAR)
        .test('reverseChargeRequiresVatId', 'Reverse charge requires a VAT ID', function (value) {
          if (value !== TaxProcedure.REVERSE_CHARGE) {
            return true;
          }
          if (this.parent.type === InvoiceType.REVERSAL) {
            return true;
          }
          const receiverVatId = normalizeVatId(this.parent.receiverData?.vatId);
          if (receiverVatId) {
            return true;
          }
          if (!options?.counterpartyVatId) {
            return false;
          }
          return !!normalizeVatId(toRef(options.counterpartyVatId).value);
        }),
    },
    [
      ['receiverData', 'customerId'],
      ['bookingId', 'counterpartyType'],
    ],
  );
}

export const invoiceCreateSchema = createInvoiceCreateSchema();

export const invoiceUpdateSchema = yup.object({
  comment: yup.string().optional(),
  counterpartyType: yup.string().oneOf(Object.values(CounterpartyType)).optional().nullable(),
  positions: yup.array(
    yup.object({
      title: yup.string().required(),
      amount: yup.number().required(),
      unitPrice: yup.number().required(),
      tax: yup.number().required(),
      lane: yup.string().optional(),
      counterpartyType: yup.string().optional(),
    }),
  ),
});
